<template>
  <el-dialog
    :visible="true"
    :fullscreen="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
  >
    <div slot="title">
      <span v-if="type === 'add'">Új rakodási lista</span>
      <span v-if="type === 'update'">Rakodási lista módosítása</span>
      <span v-if="type === 'view'">Rakodási lista megtekintése</span>
      <el-tag
        v-if="stowage.status && (type === 'update' || type === 'view') && !loading"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <div v-loading="loading">
      <el-form
        ref="stowageForm"
        label-position="top"
        :model="stowage"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Ebben a raktárban történik a termékek kiadása. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                    placement="right"
                  >
                    <i class="el-icon-info"/>
                  </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="stowage.storage"
                style="width: 100%;"
                value-key="id"
                :disabled="stowage.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="storage_user"
              label="Raktáros"
            >
              <el-select
                v-model="stowage.storage_user"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="stowage.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="user in users"
                  :key="user.id + 'storage_user'"
                  :value="user"
                  :label="user.full_name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="delivery_date"
              label="Szállítás dátuma"
            >
              <el-date-picker
                v-model="stowage.delivery_date"
                type="date"
                :disabled="stowage.status === 'done' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="cm_delivery_note_number"
              label="CM szlev száma"
            >
              <el-input
                v-model="stowage.cm_delivery_note_number"
                :disabled="stowage.status !== 'recorded'"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              prop="customer_orders"
              label="Vevői megrendelések"
            >
              <el-select
                v-model="selectedCustomerOrders"
                value-key="id"
                style="width: 100%;"
                :multiple="true"
                :filterable="true"
                :disabled="stowage.status !== 'recorded' || type === 'view'"
                @change="loadCustomerOrderItems"
                @remove-tag="order => removeCustomerOrder(order.id)"
              >
                <el-option
                  v-for="order in filteredCustomerOrders"
                  :key="order.id"
                  :value="order"
                  :label="order.receipt_number"
                >
                  <span style="float: left; margin-right: 10px;">
                    {{ order.receipt_number }} <i class="el-icon-d-arrow-right"></i> {{ order.customer_name }}
                  </span>
                  <span style="float: right; color: #8492a6; font-size: 13px; padding-right: 20px;">
                    <el-tag v-if="!isExpired(order.delivery_date)" size="mini" type="info">Szállítás: {{ order.delivery_date | date }}</el-tag>
                    <el-tag v-if="isExpired(order.delivery_date)" size="mini" type="danger">Szállítás: {{ order.delivery_date | date }}</el-tag>
                  </span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              prop="representative_orders"
              label="Raktárközi megrendelések"
            >
              <el-select
                v-model="selectedRepresentativeOrders"
                value-key="id"
                style="width: 100%;"
                :multiple="true"
                :filterable="true"
                :disabled="stowage.status !== 'recorded' || type === 'view'"
                @change="loadRepresentativeOrderItems"
              >
                <el-option
                  v-for="order in filteredRepresentativeOrders"
                  :key="order.id + 'representativeOrder'"
                  :value="order"
                  :label="order.receipt_number"
                >
                  <span style="float: left; margin-right: 10px;">
                    {{ order.receipt_number }} <i class="el-icon-d-arrow-right"></i> {{ order.customer_name }}
                  </span>
                  <span style="float: right; color: #8492a6; font-size: 13px; padding-right: 20px;">
                    <el-tag v-if="!isExpired(order.completed_at)" size="mini" type="info">Szállítás: {{ order.completed_at | date }}</el-tag>
                    <el-tag v-if="isExpired(order.completed_at)" size="mini" type="danger">Szállítás: {{ order.completed_at | date }}</el-tag>
                  </span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <p-table
          v-for="order in stowage.customer_orders"
          :key="'asd' + order.id"
          :ref="`customerOrderItems-${order.id}`"
          :storage-id="stowage && stowage.storage && stowage.storage.id"
          :emptyLine="getEmptyLine(order.id)"
          :columns="columns"
          :opts="getTableOptions(order)"
          dialog="StowageItemDialog"
          :data="order.remaining_sales && order.remaining_sales.filter(s => s.count > 0) || order.order_items.filter(i => i.count > 0)"
        >
          <template
            slot-scope="props"
          >
            <div class="product-table-header">
              <div style="display: inline-block;">
                <strong>Vevői megrendelés:</strong>
                <receipt-link
                  :id="order.id"
                  :label="order.receipt_number"
                  type="customerOrder"
                  style="margin: -4px 3px 0 5px"
                />
                <i class="el-icon-d-arrow-right"></i> {{ order.customer_name }}
                <p style="margin: 0;">{{ order.customer_postal_code }} {{ order.customer_city }} {{
                  order.customer_address }}</p>
              </div>
              <div style="display: inline-block; float: right;">
                <el-button
                  :disabled="props.stockLoading || props.itemsLoading || stowage.status !== 'recorded' || type === 'view'"
                  icon="el-icon-refresh"
                  :loading="props.stockLoading || props.itemsLoading"
                  size="mini"
                  @click.stop.prevent="props.autoSelectProductData"
                  style="margin-top: -5px;"
                >
                  Beszállítók és szavatossági idők betöltése
                </el-button>
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="mini"
                  @click.stop.prevent="removeCustomerOrder(order.id)"
                  style="margin-top: -5px;"
                  :disabled="stowage.status !== 'recorded' || type === 'view'"
                >
                  Törlés
                </el-button>
              </div>
            </div>
          </template>
        </p-table>
        <p-table
          v-for="order in stowage.representative_orders"
          :key="'representativeOrderItems' + order.id"
          :ref="`representativeOrderItems-${order.id}`"
          :storage-id="stowage && stowage.storage && stowage.storage.id"
          :emptyLine="getEmptyLine(null, order.id)"
          :columns="columns"
          :opts="getTableOptions(order)"
          dialog="StowageItemDialog"
          :data="order.remaining_sales && order.remaining_sales.filter(s => s.count > 0) || order.order_items.filter(i => i.count > 0)"
        >
          <template
            slot-scope="props"
          >
            <div class="product-table-header">
              <div style="display: inline-block;">
                <strong>Vevői megrendelés:</strong>
                <receipt-link
                  :id="order.id"
                  :label="order.receipt_number"
                  type="customerOrder"
                  style="margin: -4px 3px 0 5px"
                />
                <i class="el-icon-d-arrow-right"></i> {{ order.customer_name }}
                <p style="margin: 0;">{{ order.customer_postal_code }} {{ order.customer_city }} {{
                  order.customer_address }}</p>
              </div>
              <div style="display: inline-block; float: right;">
                <el-button
                  :disabled="props.stockLoading || props.itemsLoading || stowage.status !== 'recorded' || type === 'view'"
                  icon="el-icon-refresh"
                  :loading="props.stockLoading || props.itemsLoading"
                  size="mini"
                  @click.stop.prevent="props.autoSelectProductData"
                  style="margin-top: -5px;"
                >
                  Beszállítók és szavatossági idők betöltése
                </el-button>
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="mini"
                  @click.stop.prevent="removeCustomerOrder(order.id)"
                  style="margin-top: -5px;"
                  :disabled="stowage.status !== 'recorded' || type === 'view'"
                >
                  Törlés
                </el-button>
              </div>
            </div>
          </template>
        </p-table>
      </el-form>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        v-if="stowage.status !== 'recorded' && stowage.status !== 'cancelled' && stowage.receipt_file"
        @click="openReceipt"
      >
        Bizonylat nyomtatása
      </el-button>
      <el-button
        :disabled="stowage.status !== 'recorded'"
        type="primary"
        :loading="buttonLoading"
        @click="store"
      >
        Mentés
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import PTable from '../../utils/PTable';
import ReceiptLink from '../../utils/ReceiptLink';

export default {
  components: {
    ReceiptLink,
    PTable,
  },
  props: ['type', 'stowageId'],
  data() {
    return {
      loading: false,
      receiptPrefix: '',
      buttonLoading: false,
      customerOrders: [],
      representativeOrders: [],
      selectedRepresentativeOrders: [],
      selectedCustomerOrders: [],
      stowage: {
        status: 'recorded',
        storage: {},
        storage_user: {},
        customer_orders: [],
        representative_orders: [],
        cm_delivery_note_number: ''
      },
      rules: {
        storage_user: [
          {
            required: true,
            message: 'Raktáros kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        delivery_date: [
          {
            required: true,
            message: 'Szállítási dátum kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        cm_delivery_note_number: [
          {
            max: 255,
            message: 'A CM szállítólevél szám maximális hossza 255 karakter',
            trigger: 'blur'
          }
        ],
      },
      columns: [
        {
          label: 'Termék',
          prop: 'name',
          minWidth: 200,
        },
        {
          label: 'Kiszerelés',
          prop: 'pack_type_name',
        },
        {
          label: 'Beszállító',
          prop: 'supplier_name',
          showTooltip: true
        },
        {
          label: 'Szavatossági idő',
          prop: 'expiration_date',
          minWidth: 110
        },
        {
          label: 'Mennyiség',
          prop: 'count',
          align: 'right'
        },
      ],
    };
  },
  computed: {
    filteredRepresentativeOrders() {
      let targetStorageId = this.stowage.storage ? this.stowage.storage.id : null;
      return this.representativeOrders.filter(representativeOrder => representativeOrder.source_storage_id === targetStorageId);
    },
    filteredCustomerOrders() {
      let storageId = this.stowage.storage ? this.stowage.storage.id : null;
      return this.customerOrders.filter(customerOrder => customerOrder.storage_id === storageId);
    },
    users() {
      return this.$store.getters['user/users'];
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
  },
  async mounted() {
    this.loading = true;
    const requiredFetches = [];
    requiredFetches.push(this.$store.dispatch('customerOrder/fetchRemainingOrders'));
    requiredFetches.push(this.$store.dispatch('representativeOrder/fetchRemainingOrders'));
    requiredFetches.push(axios.get('api/receipt-prefixes/stowage'));
    if (!this.users.length) {
      requiredFetches.push(this.$store.dispatch('user/fetchAll', ''));
    }
    if (!this.storages.length) {
      requiredFetches.push(this.$store.dispatch('storage/fetchAll', ''));
    }
    const [customerOrders, representativeOrders, receiptPrefix] = await Promise.all(requiredFetches);
    this.customerOrders = customerOrders;
    this.representativeOrders = representativeOrders;
    this.receiptPrefix = receiptPrefix.full_prefix;
    if (this.stowageId) {
      this.stowage = await this.$store.dispatch('stowage/fetch', this.stowageId);
      this.stowage.customer_orders.forEach(order => this.customerOrders.push(order));
      this.stowage.representative_orders.forEach(order => this.representativeOrders.push(order));
      this.stowage.customer_orders = this.stowage.customer_orders.map(order => ({
        ...order,
        remaining_sales: null,
        order_items: this.stowage.order_items.filter(item => item.customer_order_id === order.id),
      }));
      this.stowage.representative_orders = this.stowage.representative_orders.map(order => ({
        ...order,
        remaining_sales: null,
        order_items: this.stowage.order_items.filter(item => item.representative_order_id === order.id),
      }));
      this.selectedRepresentativeOrders = this.stowage.representative_orders || [];
      this.selectedCustomerOrders = this.stowage.customer_orders || [];
    } else {
      this.stowage.storage = this.storages[0];
      this.stowage.storage_user = this.$store.getters['auth/user'];
    }
    this.loading = false;
  },
  methods: {
    async loadCustomerOrderItems(customerOrders) {
      for (const order of customerOrders) {
        this.$set(this.stowage, 'delivery_date', order.delivery_date);
        if (!order.isFetched) {
          const items = await axios.get(`api/customer-orders/remaining-orders/${order.id}/items`);
          order.isFetched = true;
          order.order_items = items;
          this.stowage.customer_orders.push(order);
        }
      }
    },
    async loadRepresentativeOrderItems(representativeOrders) {
      for (const order of representativeOrders) {
        this.$set(this.stowage, 'delivery_date', order.completed_at);
        if (!order.isFetched) {
          const items = await axios.get(`api/representative-orders/remaining-orders/${order.id}/items`);
          order.isFetched = true;
          order.order_items = items;
          this.stowage.representative_orders.push(order);
        }
      }
    },
    removeCustomerOrder(id) {
      this.stowage.customer_orders = this.stowage.customer_orders.filter(order => order.id !== id);
      this.selectedCustomerOrders = this.selectedCustomerOrders.filter(order => order.id !== id);
    },
    removeRepresentativeOrder(id) {
      this.stowage.representative_orders = this.stowage.representative_orders.filter(order => order.id !== id);
      this.selectedRepresentativeOrders = this.selectedRepresentativeOrders.filter(order => order.id !== id);
    },
    isExpired(date) {
      return moment(date)
        .isBefore();
    },
    handleClose() {
      if (this.type === 'view') {
        return this.$router.replace('/sales/stowages');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
        .then(() => {
          this.$router.replace('/sales/stowages');
        })
        .catch(() => {
        });
    },
    async store() {
      this.$refs.stowageForm.validate(async valid => {
        if (valid) {
          this.buttonLoading = true;
          if (!this.stowage.customer_orders.length && !this.stowage.representative_orders.length) {
            this.buttonLoading = false;
            return this.$messageWrapper.warning('Minimum 1 rendelés hozzáadása kötelező.');
          }
          const items = [];
          const customerOrders = this.stowage.customer_orders.map(order => {
            const orderItems = this.$refs[`customerOrderItems-${order.id}`][0].getItems();
            orderItems.forEach(orderItem => {
              items.push({
                ...orderItem,
                id: undefined,
                customer_order_id: order.id,
              });
            });
            return {
              id: order.id
            };
          });
          const representativeOrders = this.stowage.representative_orders.map(order => {
            const orderItems = this.$refs[`representativeOrderItems-${order.id}`][0].getItems();
            orderItems.forEach(orderItem => {
              items.push({
                ...orderItem,
                id: undefined,
                representative_order_id: order.id,
              });
            });
            return {
              id: order.id,
            };
          });
          const data = {
            id: this.stowage.id,
            delivery_date: this.stowage.delivery_date,
            storage_id: this.stowage.storage.id,
            storage_user_id: this.stowage.storage_user.id,
            order_items: items,
            customer_orders: customerOrders,
            representative_orders: representativeOrders,
            cm_delivery_note_number: this.stowage.cm_delivery_note_number
          };
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('stowage/create', data);
            } else if (this.type === 'update') {
              await this.$store.dispatch('stowage/update', data);
            }
            this.$store.dispatch('stowage/fetchAll');
            this.buttonLoading = false;
            this.$router.replace('/sales/stowages');
          } catch (e) {
          }
          this.buttonLoading = false;
        }
      });
    },
    getStatus() {
      switch (this.stowage.status) {
        case 'recorded':
          return {
            label: 'Rögzítve',
            tag: 'info',
          };
        case 'sent':
          return {
            label: 'Rakodási listán',
            tag: 'primary',
          };
        case 'cancelled':
          return {
            label: 'Lemondva',
            tag: 'danger',
          };
        case 'done':
          return {
            label: 'Elküldve',
            tag: 'success',
          };
      }
    },
    getTableOptions(order) {
      return {
        currency: order.currency_code || 'HUF',
        addItem: this.stowage.status === 'recorded' && this.type !== 'view',
        updateItem: this.stowage.status === 'recorded' && this.type !== 'view',
        deleteItem: this.stowage.status === 'recorded' && this.type !== 'view',
        displayStock: this.stowage.status === 'recorded' && this.type !== 'view',
        editableFields: [],
      };
    },
    getEmptyLine(customerOrderId, representativeOrderId) {
      return {
        count: 1,
        unit_price: 0,
        net_price: 0,
        count_per_pack: 1,
        vat: 27,
        expiration_date: null,
        comment: '',
        name: '',
        product_pack_type_id: null,
        product_id: null,
        supplier_id: null,
        supplier_name: null,
        customer_order_id: customerOrderId,
        representative_order_id: representativeOrderId,
        vat_value: 0,
        gross_price: 0,
      };
    }
  },
};
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;

    > div {
      display: inline-block;
    }
  }

  .product-table-header {
    border-top: 1px dotted #dadfe7;
  }

  .product-table-footer {
    border-bottom: 1px dotted #dadfe7;
    text-align: right;
    font-weight: 700;
  }

  .product-table-header, .product-table-footer {
    border-left: 1px dotted #dadfe7;
    border-right: 1px dotted #dadfe7;
    padding: 15px 10px;
  }
</style>
